import styles from '@/styles/Shop.module.scss'
import BaseLayout from '@/layout/BaseLayout/BaseLayout'
import Head from 'next/head'
import React from 'react'
import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import RimSearch from '@/components/search/RimSearch/RimSearch'
import { getPageTitle } from '@/utils/helpers'
import { useTranslation } from 'next-i18next'
import PageInfo from '@/components/common/PageInfo/PageInfo'
import Raw from '@/components/common/Raw/Raw'
import GalleryViewSearchResult from '@/components/search/GalleryView/GalleryView'
import { wrapper } from '@/store/store'
import { getCategoryHighlight, getRunningQueriesThunk, useGetCategoryHighlightQuery } from '@/api/categoryHighlights'
import { set } from 'lodash'

type Props = {}

const Rims = (props: Props) => {
  const { t } = useTranslation('common')
  const { data } = useGetCategoryHighlightQuery(220)
  const [hasSearch, setHasSearch] = React.useState<boolean>(false)

  return (
    <BaseLayout>
      <Head>
        <title>{getPageTitle(t('rims:title'))}</title>
        <meta name="description" content={t('rims:description') || ''} />
      </Head>
      <main>
        <PageInfo>
          <h1>{t('rims:header')}</h1>
          <Raw content={t('rims:pageDescription')} />
        </PageInfo>
        <RimSearch
          onHasSearch={setHasSearch}
          onVehicleChange={() => setHasSearch(true)}
        />

        {!hasSearch && (
          <>
            <h3 className={styles.randomArticles}>{t('rims:randomProducts')}</h3>
            <GalleryViewSearchResult
              items={data || []}
              listId={"highlight_rims"}
              listName={"Highlight Rims"}
            />
          </>
        )}

        <PageInfo languageCode='SV'>
          <h3>Varför bör du välja Statusfälgar för dina nya fälgar och däck?</h3>
          <ul>
            <li><strong>Brett utbud:</strong> Vi har ett omfattande sortiment av fälgar och däck som passar perfekt till pickups, SUV:ar, 4x4 och stadsjeepar. Oavsett vilken modell du har, hittar du rätt fälgar och däck för din bil.</li>
            <li><strong>Hög kvalitet:</strong> Vi erbjuder produkter av högsta kvalitet från välkända varumärken som Dirt A.T Wheels / Dirt Wheels och Blackbear däck. Våra fälgar och däck är konstruerade för att hålla länge och ge överlägsen prestanda både på vägen och i terrängen.</li>
            <li><strong>Prisvärdhet:</strong> Vi erbjuder konkurrenskraftiga priser utan att kompromissa med kvaliteten. Vi förstår vikten av att hålla sig inom budget och strävar efter att erbjuda prisvärda alternativ för alla våra kunder.</li>
            <li><strong>Expertis och erfarenhet:</strong> Med vår långa erfarenhet inom branschen har vi kunskapen och expertisen för att hjälpa dig att hitta de bästa fälgarna och däcken för ditt fordon. Vi är här för att guida dig och ge dig professionell rådgivning längs vägen.</li>
            <li><strong>Snabb och smidig leverans:</strong> Vi erbjuder snabb och pålitlig leverans för att du ska kunna njuta av dina nya fälgar och däck så snabbt som möjligt. Vi strävar efter att ge dig en bekväm och problemfri köpupplevelse, från beställning till leverans.</li>
          </ul>

          <p>Välkommen till Statusfälgar. Upptäck varför vi är det självklara valet för fordonägare som söker bra kvalitet, ett stort utbud av däck och fälgar, samt bra support och kundservice.</p>

          <h3>Fälgar från ledande varumärken för överlägsen kvalitet och prestanda</h3>
          <p>Vi är stolta över att kunna erbjuda fälgar och däck från några av branschens <strong>mest erkända varumärken</strong>. Vårt sortiment inkluderar fälgar och däck från <strong>Dirt A.T Wheels / Dirt Wheels</strong> och <strong>Blackbear</strong>, varumärken som är kända för sin <strong>överlägsna kvalitet, hållbarhet och prestanda</strong>.</p>
          <p>Med våra fälgar och däck kan du vara trygg i att du får produkter som är konstruerade för att möta de mest krävande förhållandena och förse din bil med enastående prestanda på vägarna.</p>


          <h2>En guide till att köpa fälgar online - Hitta perfekta fälgar till pickups, SUV:ar och stadsjeepar</h2>
          <p>Att köpa fälgar online bör vara en smidig och bekväm process som sparar både tid och pengar. Här är en guide som hjälper dig att göra det bästa valet när du köper fälgar till pickups, SUV:ar eller stadsjeepar online, så att du kan hitta rätt fälg för din bil.</p>
          <ul>
            <li><strong>Bestäm din fälgstorlek och specifikationer:</strong> Innan du börjar leta efter fälgar online är det viktigt att du känner till din bilens fälgstorlek och dess specifikationer. Du kan hitta denna information antingen i din bils handbok eller på de befintliga fälgarna. De viktigaste specifikationerna att notera är fäljdiametern, bultcirkeln, inpressningsdjupet (ET) och navhålets diameter. Genom att veta dessa detaljer kan du filtrera sökresultaten och hitta fälgar som passar perfekt till din bil.</li>
            <li><strong>Bestäm din budget:</strong> Innan du börjar shoppa är det också viktigt att bestämma din budget för fälgarna. Både fälgar och däck kan variera mycket i pris beroende på märke, material och design. Genom att ha en klar budget i åtanke kan du snabbt filtrera bort alternativ som inte passar din ekonomi och fokusera på de som gör det.</li>
            <li><strong>Utforska olika fälgmärken och modeller:</strong> När du har bestämt din fälgstorlek och budget är det dags att utforska olika fälgmärken och modeller. Det finns många välkända och pålitliga märken på marknaden, såsom Dirt A.T Wheels / Dirt Wheels och Blackbear däck, som alla erbjuder högkvalitativa fälgar och däck.</li>
            <li><strong>Tänk på din körstil och behov:</strong> När du väljer fälgar är det viktigt att tänka på din körstil och behov. Om du till exempel kör mycket i terräng eller off-road, kan det vara fördelaktigt att välja en fälg som är särskilt utformad för sådana förhållanden. Å andra sidan, om du kör mestadels på vägar i stadsmiljö, kanske du vill fokusera på fälgar som istället ger en bekväm och tyst körning. Tänk också på faktorer som vikt, aerodynamik och underhåll när du väljer dina fälgar.</li>
          </ul>


          <h2>Stilsäkra fälgar som passar din pickup, SUV eller stadsjeep perfekt</h2>
          <p>Vi förstår att utseendet på ditt fordon är av stor betydelse för dig. Därför erbjuder vi ett omfattande sortiment av fälgar i olika stilar, färger och storlekar. Oavsett om du föredrar en mer traditionell design eller en modern och aggressiv look, har vi fälgar och hjul som kommer att passa din personliga smak och lyfta fram utseendet på din pickup, SUV eller stadsjeep.</p>

          <h3>Fälgar för alla väderförhållanden och terrängtyper</h3>
          <p>Som ägare till en pickup vet du att den inte bara används på vägen. Du kanske tar den ut på terrängen, genom lera och vattenpölar, eller kanske i snöiga förhållanden. Våra fälgar är konstruerade för att klara av alla utmaningar, och ge dig pålitlig prestanda oavsett väderförhållande och terrängtyp.</p>
          <p>Oavsett om du behöver hjul med utmärkt grepp och dragkraft eller hjul som är speciellt anpassade för off-road-körning, hittar du dessa hos oss på Statusfälgar.</p>


          <h2>En enkel och bekväm upplevelse för dig som söker fälgar till pickups</h2>
          <p>Köpet av fälgar till din bil bör vara en smidig och bekväm process. Därför har vi skapat en <strong>användarvänlig webbplats</strong> som gör det enkelt för dig att hitta rätt fälgar för din specifika modell. Med vårt <strong>intuitiva gränssnitt</strong> och tydliga navigering kan du snabbt filtrera och söka efter fälgar som passar perfekt till din bil.</p>
          <p>På Statusfälgar är vi dedikerade till att göra köpet av fälgar och hjul till din bil enkelt och smidigt. Vi strävar efter att erbjuda en <strong>användarvänlig upplevelse på vår webbplats</strong>, kombinerat med <strong>enastående kundsupport</strong>, så att du kan göra ditt val med både självförtroende och trygghet.</p>
          <p><strong>Utforska vårt sortiment online idag</strong> och upptäck varför vi är det självklara valet för däck och fälgar till din pickup.</p>

          <h3>Stort sortiment av högkvalitativa fälgar och däck för din pickup, SUV eller 4x4</h3>
          <p>På Statusfälgar är vi stolta över att vara en av Sveriges största leverantörer av fälgar och hjul. Vi erbjuder ett brett utbud av fälgar som passar perfekt till <strong>pickups, SUV:ar, 4x4</strong> och stadsjeepar, från varumärken som <strong>Dirt A.T Wheels / Dirt Wheels och Blackbear däck</strong>, som alla innebär <strong>produkter av högsta kvalitet och prestanda</strong>.</p>
          <p>Oavsett om du vill ge din pickup en personlig touch eller förbättra dess prestanda, har vi rätt fälg för dig. <strong>Utforska vårt sortiment online</strong> och hitta de perfekta fälgarna till just din modell. Om du har några frågor är vårt kundsupport redo att hjälpa dig.</p>
          <p><strong>Gör din pickup till något extra</strong> med Statusfälgar - din pålitliga partner för både fälg och däck.</p>
        </PageInfo>
      </main>
    </BaseLayout>
  )
}

export default Rims

export const getServerSideProps = wrapper.getServerSideProps(
  (store) => async (context) => {
    store.dispatch(getCategoryHighlight.initiate(220))
    await Promise.all(store.dispatch(getRunningQueriesThunk()))

    // Fetch translations for static rendering
    const translations = await serverSideTranslations(context.locale || 'en', [
      'common',
      'rims',
      'shop',
      'footer',
    ])

    return {
      props: {
        ...translations,
      },
    }
  }
)